import { useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useCollateralSubmit } from './useCollateralSubmit'
import { useAssetBalance } from 'modules/pools/hooks/useAssetBalance'
import { useAssetsData } from 'modules/pools/hooks/useAssetsData'

import { Text } from 'shared/ui/common/Text'
import { InputControl } from 'shared/ui/controls/Input'
import { SelectControl } from 'shared/ui/controls/Select'
import { InputMaxAction } from 'shared/ui/controls/InputMaxAction'
import { FormSubmitter } from 'shared/ui/common/FormSubmitter'
import { Form } from 'shared/ui/controls/FormNetworkGuarded'
import {
  FormLockedValue,
  FormLockedValuesList,
} from 'shared/ui/common/FormLockedValue'
import {
  FormInfoFrame,
  FormInfoFramesList,
  FormInfoItem,
} from 'shared/ui/common/FormInfoFrame'
import { EstimatedGasFee } from 'modules/blockChain/ui/EstimatedGasFee'
import { FormTransactionRow } from 'modules/blockChain/ui/FormTransactionRow'

import * as formErrors from 'shared/constants/formErrors'
import {
  poolAssetOptions,
  getPoolAssetIcon,
  PoolAsset,
  getERCAssetAddress,
} from 'modules/pools/constants/poolAssets'
import type { FormValues, SuccessData } from './types'
import { formatAsset } from 'modules/pools/utils/formatAsset'
import { strToWei, weiToStr } from 'modules/blockChain/utils/parseWei'
import {
  CollateralLockCode,
  COLLATERAL_LOCK_CODES_MESSAGES,
} from 'modules/pools/constants/lockCodes'

import s from './FormCollateral.module.scss'
import { WarningCard } from 'shared/ui/common/WarningCard'

const ASSET_OPTIONS = [poolAssetOptions.WETH, poolAssetOptions.USDC]

const ACTION_OPTIONS: { label: string; value: FormValues['action'] }[] = [
  {
    value: 'add',
    label: 'Add',
  },
  {
    value: 'withdraw',
    label: 'Withdraw',
  },
]

type Props = {
  action: string | undefined
  collateralAsset: PoolAsset
  totalCollateral: string
  maxCollateralToWithdraw: string
  isDataLoading: boolean
  lockCode: CollateralLockCode | undefined
  onSuccess: (res: SuccessData) => void
}

export function FormCollateral({
  action: actionProp,
  collateralAsset,
  totalCollateral,
  maxCollateralToWithdraw,
  isDataLoading,
  lockCode,
  onSuccess,
}: Props) {
  const { chainId, isChainUnsupported } = useWeb3()
  const { getAssetDecimals } = useAssetsData()
  const assetAddress = getERCAssetAddress(collateralAsset, chainId)
  const assetDecimals = getAssetDecimals(collateralAsset)

  const formMethods = useForm<FormValues>({
    shouldUnregister: false,
    defaultValues: {
      asset: collateralAsset,
      amount: '',
      action: ACTION_OPTIONS[0].value,
    },
  })
  const { watch, setValue, trigger, getValues } = formMethods
  const actionType = watch('action')

  const {
    submit,
    isSubmitting,
    isLocked,
    estimatedRefillGas,
    estimatedWithdrawalGas,
    txAllowance,
    unlockForm,
  } = useCollateralSubmit({
    onSuccess,
    formMethods,
    collateralAddress: assetAddress,
  })

  const isNoCollateralToWithdraw = strToWei(
    maxCollateralToWithdraw,
    assetDecimals,
  ).isZero()

  useEffect(() => {
    if (
      actionProp === 'add' ||
      (isNoCollateralToWithdraw && actionType !== 'add')
    ) {
      setValue('action', 'add')
    } else if (actionProp === 'withdraw' && !isNoCollateralToWithdraw) {
      setValue('action', 'withdraw')
    }
  }, [actionProp, isNoCollateralToWithdraw, actionType, setValue])

  const isRefillMode = actionType === 'add'

  const { data: balanceWei } = useAssetBalance(collateralAsset)
  const maxAmountToAdd = balanceWei && weiToStr(balanceWei, assetDecimals)
  const maxAmount = isRefillMode ? maxAmountToAdd : maxCollateralToWithdraw

  const handleClickMaxAmount = useCallback(() => {
    if (!maxAmount) return
    setValue('amount', maxAmount)
  }, [maxAmount, setValue])

  useEffect(() => {
    setValue('amount', '')
  }, [actionType, setValue])

  /*
    This useEffect triggers every time
    some of the deps of amount.validate func changes.
    That way we handle validation not only on value change, but also
    on every one of the deps change.
  */
  useEffect(() => {
    if (getValues('amount') !== '') {
      setTimeout(() => trigger('amount'), 10)
    }
  }, [trigger, getValues, assetDecimals, maxAmount, collateralAsset])

  const estimatedGas = isRefillMode
    ? estimatedRefillGas
    : estimatedWithdrawalGas

  return (
    <Form formMethods={formMethods} onSubmit={submit}>
      {lockCode ? (
        <Text isCentered color="error" className={s.errorMessage}>
          {COLLATERAL_LOCK_CODES_MESSAGES[lockCode]}
        </Text>
      ) : isRefillMode ? (
        <WarningCard
          title={
            <>
              V1 will be shut down soon. Please use{' '}
              <a
                href="https://roci.fi/app"
                target="_blank"
                className={s.v2Link}
                rel="noreferrer"
              >
                RociFi V2
              </a>
              .
            </>
          }
        />
      ) : null}
      {!isLocked && (
        <>
          <SelectControl
            readOnly
            name="asset"
            placeholder="Asset"
            options={ASSET_OPTIONS}
            rules={{ required: formErrors.required }}
          />
          <SelectControl
            name="action"
            placeholder="Action"
            options={ACTION_OPTIONS}
            rules={{ required: formErrors.required }}
            readOnly={
              isNoCollateralToWithdraw || !!lockCode || isChainUnsupported
            }
            testId="collateral-form-action-select"
          />

          <InputControl
            name="amount"
            placeholder={`Amount to ${actionType}`}
            onlyNumber
            readOnly={!!lockCode || isChainUnsupported || isRefillMode}
            rules={{
              required: formErrors.required,
              validate: (val: string) =>
                formErrors.weiParsing(val, assetDecimals) ||
                formErrors.greater(val, 0) ||
                formErrors.notMore(val, Number(maxAmount), collateralAsset) ||
                true,
            }}
            testId="collateral-form-amount-input"
            action={
              maxAmount && (
                <InputMaxAction
                  isDisabled={!!lockCode}
                  onClick={handleClickMaxAmount}
                  testId="collateral-form-max-amount-button"
                />
              )
            }
          />
        </>
      )}

      {isLocked && (
        <FormLockedValuesList>
          <FormLockedValue
            label="Asset"
            name="asset"
            getIcon={getPoolAssetIcon}
          />
          <FormLockedValue
            label={`Amount to ${actionType}`}
            name="amount"
            formatValue={v => formatAsset(v, collateralAsset)}
            showTooltipUnformatted
          />
        </FormLockedValuesList>
      )}

      {!isLocked && (
        <FormInfoFramesList>
          <FormInfoFrame>
            <FormInfoItem
              label="Deposited to RociFi"
              value={formatAsset(totalCollateral, collateralAsset)}
              sign={collateralAsset}
              tooltip={totalCollateral}
            />
            <FormInfoItem
              label={isRefillMode ? 'Your balance' : 'Max withdrawal amount'}
              value={formatAsset(maxAmount, collateralAsset)}
              sign={collateralAsset}
              tooltip={maxAmount}
            />
          </FormInfoFrame>
        </FormInfoFramesList>
      )}

      {isLocked && isRefillMode && (
        <div>
          <FormTransactionRow
            label="Token spending approval"
            tx={txAllowance}
          />
        </div>
      )}

      {isLocked && estimatedGas && <EstimatedGasFee value={estimatedGas} />}

      <FormSubmitter
        isLocked={isLocked}
        isDisabled={!!lockCode || isChainUnsupported || isRefillMode}
        isSubmitting={isSubmitting || isDataLoading}
        firstStepText={isRefillMode ? 'Add' : 'Withdraw'}
        submitTestId="collateral-form-submit-button"
        confirmTestId="collateral-form-confirm-button"
        onClickUnlock={unlockForm}
      />
    </Form>
  )
}
